import { useState } from "react";
import { ValidTimezone, ValidTimezoneLabel } from "../../common/Port";
import { Vessel } from "../../common/Vessel";
import { useGetVesselsQuery, useCreatePortMutation } from "../../redux/api/vessel";
import { Autocomplete, Box, Button, CircularProgress, MenuItem, OutlinedInput, Select, SelectChangeEvent, TextField } from "@mui/material";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import { portCreateParams } from "../../common/Vessel";

// Port Create Component Params
type portCreateComponentParams = {
    port: string;
    timezone: ValidTimezone | null;
    vessels: Vessel[];
};

const PortCreate = (props: {
    vessels?: Vessel[];
    onClose?: (bol: boolean) => void;
}) => {
    const { data: vessels, isLoading: isVesselLoading } = useGetVesselsQuery({});
    const [createPort] = useCreatePortMutation();
    const [searchParams, setSearchParams] = useState<portCreateComponentParams>({
        vessels: !!props?.vessels ? props.vessels : [],
        port: "",
        timezone: null,
    });

    if (isVesselLoading) {
        return (
            <Box sx={{ display: "flex" }}>
                <CircularProgress />
            </Box>
        );
    }

    const handleChange = (event: SelectChangeEvent<number[]>) => {
        const {
            target: { value },
        } = event;
        const selectedVessels: Vessel[] = [];
        if (typeof value != "string") {
            value.forEach((id) => {
                if (!!vessels) {
                    const currVessel = vessels.find((v) => v.id === id);
                    if (!!currVessel) {
                        selectedVessels.push(currVessel);
                    }
                }
            });
        }
        setSearchParams({
            ...searchParams,
            vessels: selectedVessels,
        });
    };

    const closeDialog = () => {
        if (!!props.onClose) {
            props.onClose(false);
        }
    };


    return (
        <div>
            <div className="flex align-items-center justify-content-btwn mb-1">
                <h2>Port Creation</h2>
                {!!props?.onClose && (
                    <div>
                        <Button onClick={closeDialog}>
                            <CloseIcon />
                        </Button>
                    </div>
                )}
            </div>
            {/* note about this Select: MenuItem cannot accept Objects, so we have to track IDs and map them out */}
            <div className="flex-col g-2">
                <div className="flex-col g-0">
                    <label className="ft-12 b-600">Vessel</label>
                    <Select
                        className="wd-300"
                        id="rating"
                        multiple
                        displayEmpty
                        // @ts-ignore ignore this type check since typescript cannot read that undefined is impossible from the filter() function
                        value={searchParams.vessels.filter((v) => !!v.id).map((v) => v.id)}
                        onChange={handleChange}
                        input={<OutlinedInput />}
                        renderValue={(selected) => {
                            if (!selected || selected.length === 0) {
                                return (
                                    <div className="react-select">Vessels (Multi-Select)</div>
                                );
                            }

                            if (!!vessels) {
                                return selected
                                    .filter((id) => !!vessels.find((v) => v.id === id))
                                    .map((id) => vessels?.find((v) => v.id === id)?.name)
                                    .join(", ");
                            } else {
                                return "";
                            }
                        }}
                        disabled={!!props?.onClose}
                    >
                        {(vessels ? vessels : []).filter(v => !v.disabled).map((vessel) => (
                            <MenuItem key={vessel.id} value={vessel.id}>
                                {vessel.name}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
                <div className="flex-col g-0">
                    <label className="ft-12 b-600">Port</label>
                    <TextField
                        className="wd-300"
                        id="port"
                        onChange={(event) => {
                            setSearchParams({
                                ...searchParams,
                                port: event.target.value,
                            });
                        }}
                        value={searchParams?.port}
                        variant="outlined"
                        placeholder="Type Port"
                        size="small"
                    />
                </div>
                <div className="flex-col g-0">
                    <label className="ft-12 b-600">Timezone</label>
                    <Autocomplete
                        className="wd-300"
                        options={Object.values(ValidTimezone)}
                        getOptionLabel={(option) =>
                            ValidTimezoneLabel[option as ValidTimezone]
                        }
                        value={searchParams?.timezone}
                        renderInput={(params) => (
                            <TextField
                                className="timezone"
                                {...params}
                                placeholder="Type Timezone"
                            />
                        )}
                        onChange={(event, value) => {
                            setSearchParams({
                                ...searchParams,
                                timezone: value as ValidTimezone,
                            });
                        }}
                    />
                </div>
                <div className="flex g-2">
                    {!!props?.onClose && (
                        <Button onClick={closeDialog} className="outline-button-small">
                            Cancel
                        </Button>
                    )}

                    <Button
                        className="blue-button"
                        onClick={() => {
                            const portCreateParams: portCreateParams = {
                                port: searchParams.port,
                                timezone: !!searchParams.timezone
                                    ? searchParams.timezone.valueOf()
                                    : null,
                                // @ts-ignore ignore this type check since typescript cannot read that undefined is impossible from the filter() function
                                vesselIds: searchParams.vessels
                                    .filter((v) => !!v.id)
                                    .map((v) => v.id),
                            };
                            createPort(portCreateParams).then((response) => {
                                if (!("error" in response)) {
                                    toast.success(<p>Port created successfully</p>);
                                    setSearchParams({
                                        vessels: searchParams.vessels,
                                        port: "",
                                        timezone: null,
                                    });
                                }
                            });
                        }}
                    >
                        Add Port
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default PortCreate;